import React from "react";
import styled from "styled-components";

import * as colors from "../../styles/colors";

const Figure = styled.figure`
  margin: 28px auto;
  padding: 0;
  max-width: 100%;
  width: 100%;
`;

const Container = styled.div`
  background: #0c0f14;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: stretch;

  @media (max-width: 820px) {
    flex-direction: column;
    padding: 16px;
    gap: 32px;
  }
`;

const Column = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0 50px;

  @media (max-width: 820px) {
    padding: 0;
  }
`;

const ColumnHeader = styled.div`
  text-align: center;
  margin-bottom: 6px;
`;

const ColumnTitle = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${(p) => p.$color || "#cbd5e1"};
  line-height: 1.2;
  margin-bottom: 3px;

  @media (max-width: 820px) {
    font-size: 20px;
  }
`;

const ColumnSubtitle = styled.div`
  font-size: 13px;
  font-style: italic;
  color: ${(p) => p.$color || "#94a3b8"};
  opacity: ${(p) => p.$opacity || 1};
`;

const Box = styled.div`
  background: ${(p) => p.$bg || "#1c2028"};
  border: 1px ${(p) => p.$borderStyle || "solid"}
    ${(p) => p.$border || "#3f4651"};
  border-radius: 8px;
  padding: 14px 16px;
  text-align: center;
`;

const BoxTitle = styled.div`
  font-size: ${(p) => p.$size || 15}px;
  font-weight: 600;
  color: #f1f5f9;
  line-height: 1.3;
  margin-bottom: 4px;
`;

const BoxBody = styled.div`
  font-size: 13px;
  color: ${(p) => p.$color || "#94a3b8"};
  line-height: 1.5;
`;

const PillRow = styled.div`
  display: flex;
  gap: 6px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: ${(p) => (p.$marginTop != null ? p.$marginTop : 8)}px;
  margin-bottom: ${(p) => (p.$marginBottom != null ? p.$marginBottom : 0)}px;
`;

const Pill = styled.span`
  display: inline-block;
  padding: ${(p) => (p.$size === "lg" ? "6px 18px" : "3px 11px")};
  border-radius: 999px;
  font-size: ${(p) => (p.$size === "lg" ? "13px" : "10.5px")};
  font-weight: 700;
  letter-spacing: 0.5px;
  background: ${(p) => p.$bg || "transparent"};
  color: ${(p) => p.$color || "#fff"};
  border: ${(p) => (p.$border ? `1px solid ${p.$border}` : "none")};
  white-space: nowrap;
  line-height: 1.4;
`;

const BulletList = styled.div`
  text-align: center;
  margin-top: ${(p) => (p.$marginTop != null ? p.$marginTop : 8)}px;

  > div {
    font-size: 13px;
    color: #cbd5e1;
    line-height: 1.55;
  }
`;

const ArrowDown = styled.div`
  width: 1.5px;
  height: 18px;
  background: ${(p) => p.$color || "#6b7280"};
  margin: 0 auto;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    left: -4px;
    bottom: -5px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid ${(p) => p.$color || "#6b7280"};
  }
`;

const FooterNote = styled.div`
  text-align: center;
  font-size: 12px;
  font-style: italic;
  color: ${(p) => p.$color || "#64748b"};
  margin-top: 12px;
  line-height: 1.55;
`;

const Caption = styled.figcaption`
  margin-top: 12px;
  padding: 0 4px;
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.muted};
  text-align: center;
  font-style: italic;

  @media (max-width: 640px) {
    font-size: 13px;
    text-align: left;
    margin-top: 10px;
    padding: 0;
  }
`;

const STEP_VARIANTS = {
  risk: {
    bg: "#2a1414",
    border: "#ef4444",
    pillBg: "#ef4444",
    pillColor: "#ffffff",
  },
  safe: {
    bg: "#1e1b3a",
    border: "#6366f1",
    pillBg: "#10b981",
    pillColor: "#062b1d",
  },
  neutralIndigo: {
    bg: "#1f1c30",
    border: "#6366f1",
  },
  warning: {
    bg: "#2a1f0a",
    border: "#f59e0b",
    borderStyle: "dashed",
  },
  neutral: {},
};

function renderBodyLines(body) {
  if (!body) return null;
  return String(body)
    .split("\n")
    .map((line, i, arr) => (
      <React.Fragment key={i}>
        {i > 0 && <br />}
        {line}
      </React.Fragment>
    ));
}

function FlowBlock({ block, accent }) {
  const isIndigo = accent === "indigo";

  if (block.type === "headset") {
    const v = isIndigo ? STEP_VARIANTS.neutralIndigo : STEP_VARIANTS.neutral;
    return (
      <Box $bg={v.bg} $border={v.border}>
        <BoxTitle>{block.title}</BoxTitle>
        <BoxBody $color={isIndigo ? "#cbd5e1" : undefined}>
          {renderBodyLines(block.body)}
        </BoxBody>
      </Box>
    );
  }

  if (block.type === "step") {
    const v = STEP_VARIANTS[block.tone] || STEP_VARIANTS.neutral;
    return (
      <Box $bg={v.bg} $border={v.border} $borderStyle={v.borderStyle}>
        <BoxTitle>{block.title}</BoxTitle>
        {block.pill && (
          <PillRow $marginBottom={8}>
            <Pill $bg={v.pillBg} $color={v.pillColor}>
              {block.pill}
            </Pill>
          </PillRow>
        )}
        {block.body && (
          <BoxBody $color="#cbd5e1">{renderBodyLines(block.body)}</BoxBody>
        )}
      </Box>
    );
  }

  if (block.type === "transport") {
    const v = STEP_VARIANTS[block.tone || "safe"] || STEP_VARIANTS.neutral;
    return (
      <Box $bg={v.bg} $border={v.border}>
        <BoxTitle>{block.title}</BoxTitle>
        {block.transports && block.transports.length > 0 && (
          <PillRow $marginTop={10} $marginBottom={8}>
            {block.transports.map((t) => (
              <Pill
                key={t}
                $bg="#312e60"
                $color="#a5b4fc"
                $border="#818cf8"
                $size="lg"
              >
                {t}
              </Pill>
            ))}
          </PillRow>
        )}
        {block.pills && block.pills.length > 0 && (
          <PillRow $marginTop={0} $marginBottom={8}>
            {block.pills.map((p) => (
              <Pill key={p} $bg="#10b981" $color="#062b1d">
                {p}
              </Pill>
            ))}
          </PillRow>
        )}
        {block.body && (
          <BoxBody $color="#cbd5e1">{renderBodyLines(block.body)}</BoxBody>
        )}
      </Box>
    );
  }

  if (block.type === "recipients") {
    const v = STEP_VARIANTS.warning;
    return (
      <Box $bg={v.bg} $border={v.border} $borderStyle={v.borderStyle}>
        <BoxTitle>{block.title}</BoxTitle>
        <BulletList $marginTop={10}>
          {block.items.map((item, i) => (
            <div key={i} style={{ color: "#fcd34d" }}>
              • {item}
            </div>
          ))}
        </BulletList>
      </Box>
    );
  }

  return null;
}

function FlowColumn({ data }) {
  const {
    name,
    subtitle,
    titleColor,
    subtitleColor,
    subtitleOpacity,
    accent,
    blocks,
    footer,
    footerColor,
  } = data;
  const arrowColor = accent === "indigo" ? "#818cf8" : "#6b7280";

  return (
    <Column>
      <ColumnHeader>
        <ColumnTitle $color={titleColor}>{name}</ColumnTitle>
        <ColumnSubtitle $color={subtitleColor} $opacity={subtitleOpacity}>
          {subtitle}
        </ColumnSubtitle>
      </ColumnHeader>
      {blocks.map((block, i) => (
        <React.Fragment key={i}>
          <FlowBlock block={block} accent={accent} />
          {i < blocks.length - 1 && <ArrowDown $color={arrowColor} />}
        </React.Fragment>
      ))}
      {footer && <FooterNote $color={footerColor}>{footer}</FooterNote>}
    </Column>
  );
}

const CROWN_COLUMN = {
  name: "Neurosity Crown",
  subtitle: "Only the Crown's owner can access the data",
  titleColor: "#a5b4fc",
  subtitleColor: "#a5b4fc",
  subtitleOpacity: 0.75,
  accent: "indigo",
  blocks: [
    {
      type: "headset",
      title: "Crown headset",
      body: "8 channels, N3 chipset\nHardware-level encryption",
    },
    {
      type: "step",
      tone: "safe",
      title: "Setup",
      pill: "NFC CLAIMED",
      body: "Tap the back of the Crown to bind it to your account. No one else can connect.",
    },
    {
      type: "step",
      tone: "safe",
      title: "On-device DSP (N3)",
      pill: "HARDWARE ENCRYPTED",
      body: "Signal is processed on the headset. Raw EEG never leaves unless you explicitly stream it.",
    },
    {
      type: "transport",
      tone: "safe",
      title: "Authenticated transport",
      transports: ["WiFi", "Bluetooth"],
      pills: ["ENCRYPTED", "AUTHENTICATED"],
      body:
        "Only apps logged into your Neurosity account can read the stream",
    },
    {
      type: "step",
      tone: "safe",
      title: "Your apps only",
      body: "Explicit grant required for any other access",
    },
  ],
  footer: "Private by architecture, not policy.",
  footerColor: "#a5b4fc",
};

export function PrivacyFlowDiagram({ left, right = CROWN_COLUMN, caption }) {
  return (
    <Figure>
      <Container>
        <FlowColumn data={left} />
        <FlowColumn data={right} />
      </Container>
      {caption && <Caption>{caption}</Caption>}
    </Figure>
  );
}
